import React, { useState, useEffect } from 'react';
import { useTranslate } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const Conseil = () => {
  const [pageData, setPageData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const { currentLanguage } = useLanguage();

  // Traductions
  const { translatedText: titleTranslated } = useTranslate(pageData?.title || "Organe d'Administration");
  const { translatedText: subtitleTranslated } = useTranslate("Les Lignages de Bruxelles");
  const { translatedText: compositionTranslated } = useTranslate("Composition de l'organe d'administration");
  const { translatedText: descriptionTranslated } = useTranslate("Découvrez les membres de l'organe d'administration.");
  const { translatedText: noMembersTranslated } = useTranslate("Les membres du conseil seront bientôt annoncés.");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Récupérer les données de la page
      const pageResponse = await fetch(`${API_BASE}/api/site/pages/conseil`);
      if (pageResponse.ok) {
        const pageData = await pageResponse.json();
        setPageData(pageData);
      }

      // Récupérer les membres du conseil
      const membersResponse = await fetch(`${API_BASE}/api/board-members`);
      if (membersResponse.ok) {
        const data = await membersResponse.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header Image */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
            alt={pageData.title}
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        >
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              {titleTranslated}
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {subtitleTranslated}
            </p>
          </div>
        </div>
      )}

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Titre si pas d'image header */}
          {!pageData?.header_image && (
            <div className="text-center mb-12">
              <h1 
                className="text-5xl font-bold mb-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {titleTranslated}
              </h1>
            </div>
          )}

          {/* Titre de la section membres */}
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold mb-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)'
              }}
            >
              {compositionTranslated}
            </h2>
            <p 
              className="text-gray-700 text-lg mb-4"
              style={{ fontFamily: '"Lora", serif' }}
            >
              {descriptionTranslated}
            </p>
            <div className="w-24 h-1 mx-auto" style={{ backgroundColor: 'rgb(196, 150, 96)' }}></div>
          </div>

          {/* Grille des membres */}
          {members.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {members.map((member) => (
                <div 
                  key={member.id} 
                  className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow flex flex-col"
                >
                  {member.photo_url ? (
                    <div className="relative h-72 bg-gray-200 flex-shrink-0 overflow-hidden">
                      <OptimizedImage
                        src={member.photo_url.startsWith('http') ? member.photo_url : `${API_BASE}${member.photo_url}`}
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                        containerClassName="w-full h-full"
                        useThumbnail={true}
                        placeholder="skeleton"
                      />
                    </div>
                  ) : (
                    <div 
                      className="h-72 flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgb(196, 150, 96, 0.1)' }}
                    >
                      <div className="text-center">
                        <div 
                          className="w-24 h-24 mx-auto rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4"
                          style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                        >
                          {member.name.charAt(0)}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-5 flex-grow bg-white">
                    <h3 
                      className="text-lg font-bold mb-1 text-center"
                      style={{ 
                        fontFamily: '"Lora", serif',
                        color: 'rgb(196, 150, 96)'
                      }}
                    >
                      {member.name}
                    </h3>
                    <p 
                      className="text-gray-700 font-semibold text-center text-sm"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      {member.title}
                    </p>
                    {member.description && (
                      <p 
                        className="text-gray-600 text-xs text-center mt-2"
                        style={{ fontFamily: '"Lora", serif' }}
                      >
                        {member.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500" style={{ fontFamily: '"Lora", serif' }}>
                {noMembersTranslated}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conseil;
